import React from "react";
import { X, Plus } from "lucide-react";
import { Character } from "./types";
import { StatCard } from "./StatCard";

interface SpellsTabProps {
	character: Character;
	updateCharacter: (path: string, value: any) => void;
}

export const SpellsTab: React.FC<SpellsTabProps> = ({
	character,
	updateCharacter,
}) => (
	<div className="space-y-8">
		{/* Spellcasting Stats */}
		<StatCard title="Spellcasting Ability">
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
				<div>
					<label className="block text-sm font-semibold mb-2 text-gray-700">
						Spellcasting Ability
					</label>
					<select
						value={character.spellcastingAbility}
						onChange={(e) =>
							updateCharacter(
								"spellcastingAbility",
								e.target.value
							)
						}
						className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200"
					>
						<option value="">None</option>
						<option value="intelligence">Intelligence</option>
						<option value="wisdom">Wisdom</option>
						<option value="charisma">Charisma</option>
					</select>
				</div>
				<div>
					<label className="block text-sm font-semibold mb-2 text-gray-700">
						Spell Save DC
					</label>
					<input
						type="number"
						value={character.spellSaveDC}
						onChange={(e) =>
							updateCharacter(
								"spellSaveDC",
								parseInt(e.target.value) || 8
							)
						}
						className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 text-center text-xl font-bold"
					/>
				</div>
				<div>
					<label className="block text-sm font-semibold mb-2 text-gray-700">
						Spell Attack Bonus
					</label>
					<input
						type="number"
						value={character.spellAttackBonus}
						onChange={(e) =>
							updateCharacter(
								"spellAttackBonus",
								parseInt(e.target.value) || 0
							)
						}
						className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200 text-center text-xl font-bold"
					/>
				</div>
			</div>
		</StatCard>

		{/* Spell Slots */}
		<StatCard title="Spell Slots">
			<div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
				{Object.entries(character.spellSlots).map(([level, slots]) => (
					<div key={level} className="text-center">
						<label className="block text-sm font-semibold mb-2 text-gray-700">
							Level {level}
						</label>
						<div className="bg-gradient-to-b from-purple-100 to-purple-200 rounded-2xl p-4 border-2 border-purple-300">
							<div className="flex items-center justify-center gap-2">
								<input
									type="number"
									value={slots.used}
									onChange={(e) =>
										updateCharacter(
											`spellSlots.${level}.used`,
											parseInt(e.target.value) || 0
										)
									}
									className="w-12 text-center text-lg font-bold bg-transparent border-b-2 border-purple-400 outline-none"
									min="0"
									max={slots.total}
								/>
								<span className="text-lg font-bold text-purple-800">
									/
								</span>
								<input
									type="number"
									value={slots.total}
									onChange={(e) =>
										updateCharacter(
											`spellSlots.${level}.total`,
											parseInt(e.target.value) || 0
										)
									}
									className="w-12 text-center text-lg font-bold bg-transparent border-b-2 border-purple-400 outline-none"
									min="0"
								/>
							</div>
						</div>
					</div>
				))}
			</div>
		</StatCard>

		{/* Spells by Level */}
		<div className="space-y-6">
			{[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((level) => (
				<StatCard
					key={level}
					title={level === 0 ? "Cantrips" : `Level ${level} Spells`}
				>
					<div className="space-y-3">
						{character.spells[
							level as keyof typeof character.spells
						].map((spell, index) => (
							<div
								key={index}
								className="flex gap-3 items-center"
							>
								<input
									type="text"
									value={spell}
									onChange={(e) => {
										const newSpells = {
											...character.spells,
										};
										newSpells[
											level as keyof typeof character.spells
										][index] = e.target.value;
										updateCharacter("spells", newSpells);
									}}
									className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors duration-200"
									placeholder="Spell name"
								/>
								<button
									onClick={() => {
										const newSpells = {
											...character.spells,
										};
										newSpells[
											level as keyof typeof character.spells
										] = newSpells[
											level as keyof typeof character.spells
										].filter((_, i) => i !== index);
										updateCharacter("spells", newSpells);
									}}
									className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
								>
									<X size={16} />
								</button>
							</div>
						))}
						<button
							onClick={() => {
								const newSpells = { ...character.spells };
								newSpells[
									level as keyof typeof character.spells
								] = [
									...newSpells[
										level as keyof typeof character.spells
									],
									"",
								];
								updateCharacter("spells", newSpells);
							}}
							className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors duration-200 font-medium"
						>
							<Plus size={14} />
							Add Spell
						</button>
					</div>
				</StatCard>
			))}
		</div>
	</div>
);
